import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./Dashboard";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Check, Loader2, Sparkles, ShieldCheck, Mail } from "lucide-react";

/* /billing/upgrade — the conversion page.
   - Shows current trial state at the top
   - Quote request form below
   - Once a quote has been recorded (status=quoted, quoted_amount set), shows the PayPal Smart Buttons
*/
export default function Upgrade() {
  const [sub, setSub] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: qs }] = await Promise.all([
        api.get("/billing/trial"),
        api.get("/billing/quote-requests"),
      ]);
      setSub(s);
      setQuotes(qs);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (loading) {
    return <div className="p-8 text-sm text-[#495057]" data-testid="upgrade-loading">Loading…</div>;
  }

  const activeQuote = quotes.find((q) => q.status === "quoted");
  const paidQuote = quotes.find((q) => q.status === "paid");

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1200px]" data-testid="upgrade-page">
      <SEO title="Upgrade — Gr8Boost ERP" description="Upgrade your Gr8Boost workspace." noindex />
      <PageHeader
        title={sub?.subscription_status === "active" ? "Manage your subscription" : "Upgrade Gr8Boost ERP"}
        subtitle={sub?.subscription_status === "trial"
          ? `${sub.trial_days_remaining ?? 0} days left in your free trial. Request a quote any time — we'll price your annual plan based on team size and modules.`
          : sub?.subscription_status === "trial_expired"
            ? "Your free trial has ended. Request a quote to reactivate your workspace."
            : "Your workspace is active. Manage payments and quotes below."}
      />

      <StatusCard sub={sub} />

      {paidQuote && (
        <div className="mt-8 border border-[#0B7A75] bg-[#0B7A75]/5 p-6" data-testid="paid-confirmation">
          <div className="flex items-start gap-3">
            <Check size={20} className="text-[#0B7A75] mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-base">Payment received — thank you.</div>
              <div className="text-sm text-[#495057] mt-1">
                ${paidQuote.quoted_amount?.toFixed(2)} {paidQuote.quoted_currency} · Your workspace is active until {sub?.paid_until?.slice(0, 10)}.
              </div>
            </div>
          </div>
        </div>
      )}

      {activeQuote && !paidQuote && (
        <PaymentSection sub={sub} quote={activeQuote} onPaid={reload} />
      )}

      {!activeQuote && !paidQuote && (
        <QuoteRequestForm onSubmitted={reload} />
      )}

      {quotes.length > 0 && <QuoteHistory quotes={quotes} />}
    </div>
  );
}

function StatusCard({ sub }) {
  if (!sub) return null;
  const tone = sub.subscription_status === "active" ? "ok"
             : sub.subscription_status === "trial" ? "neutral"
             : "alert";
  const toneCss = { ok: "border-[#0B7A75]", neutral: "border-[#0A0A0A]", alert: "border-[#D90429]" }[tone];
  return (
    <div className={`mt-8 border ${toneCss} bg-white p-6 grid sm:grid-cols-3 gap-6`} data-testid="status-card">
      <Stat label="Status" value={
        sub.subscription_status === "active" ? "Active" :
        sub.subscription_status === "trial" ? "Free trial" :
        sub.subscription_status === "trial_expired" ? "Trial ended" : sub.subscription_status
      } />
      <Stat label={sub.subscription_status === "trial" ? "Days remaining" : "Paid until"} value={
        sub.subscription_status === "trial" ? `${sub.trial_days_remaining ?? 0} / ${sub.trial_total_days}` :
        sub.paid_until ? sub.paid_until.slice(0, 10) : "—"
      } />
      <Stat label="Plan" value="Annual — quote-based" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#495057] font-mono">{label}</div>
      <div className="font-heading text-2xl font-bold tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function QuoteRequestForm({ onSubmitted }) {
  const [f, setF] = useState({
    contact_name: "", contact_email: "",
    company_size: "11-50", estimated_users: 10,
    industry: "Distribution", country: "", phone: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try { await api.post("/billing/quote-request", f); onSubmitted(); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't submit your request"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-10 border border-[#0A0A0A] bg-white" data-testid="quote-form-section">
      <div className="border-b border-[#DEE2E6] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] font-mono"><Sparkles size={12} />Step 1 of 2</div>
        <h3 className="font-heading text-2xl font-bold tracking-tighter mt-1.5">Request your annual quote</h3>
        <p className="text-sm text-[#495057] mt-1.5 max-w-2xl">Tell us a little about your team — we price annually based on user count, modules and any custom integration work. You&apos;ll receive a tailored quote by email within 1 business day.</p>
      </div>
      <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="quote-form">
        <FormField label="Your name" required value={f.contact_name} onChange={(v) => setF({ ...f, contact_name: v })} testid="quote-name" />
        <FormField label="Work email" type="email" required value={f.contact_email} onChange={(v) => setF({ ...f, contact_email: v })} testid="quote-email" />
        <FormSelect label="Company size" value={f.company_size} onChange={(v) => setF({ ...f, company_size: v })} testid="quote-size"
          options={["1-10", "11-50", "51-200", "201-500", "500+"]} />
        <FormField label="Estimated active users" type="number" value={f.estimated_users} onChange={(v) => setF({ ...f, estimated_users: Number(v) || 0 })} testid="quote-users" />
        <FormSelect label="Industry" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} testid="quote-industry"
          options={["Distribution", "Retail", "Manufacturing", "Services", "Healthcare", "Technology", "Other"]} />
        <FormField label="Country" value={f.country} onChange={(v) => setF({ ...f, country: v })} testid="quote-country" />
        <FormField label="Phone (optional)" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="quote-phone" />
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Anything we should know? (Optional)</label>
          <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="quote-notes" rows={3}
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </div>
        {err && <div className="sm:col-span-2 text-sm text-[#D90429]" data-testid="quote-error">{err}</div>}
        <div className="sm:col-span-2 flex justify-end pt-2">
          <button type="submit" disabled={saving} data-testid="quote-submit"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white px-6 py-3 text-sm font-medium">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Mail size={14} /> Request a quote</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentSection({ sub, quote, onPaid }) {
  const buttonsRef = useRef(null);
  const cardFormRef = useRef(null);
  const cardNumRef = useRef(null);
  const cardExpRef = useRef(null);
  const cardCvvRef = useRef(null);
  const cardNameRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("idle");      // idle | loading | ready | success | error
  const [errMsg, setErrMsg] = useState("");
  const [cardEligible, setCardEligible] = useState(null);  // null=loading, true=ready, false=not available
  const [submitting, setSubmitting] = useState(false);
  const cardFieldRef = useRef(null);

  useEffect(() => {
    api.get("/billing/paypal/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ configured: false }));
  }, []);

  // Load the PayPal JS SDK with BOTH buttons and card-fields components
  useEffect(() => {
    if (!config?.configured || !config.client_id) return;
    // Helper: poll until window.paypal namespace finishes initializing (script.onload
    // fires slightly before paypal becomes available)
    const waitForPaypal = (tries = 30) => {
      if (window.paypal && window.paypal.Buttons) { setStatus("ready"); return; }
      if (tries <= 0) { setStatus("error"); setErrMsg("PayPal SDK didn't initialize."); return; }
      setTimeout(() => waitForPaypal(tries - 1), 100);
    };
    const existing = document.querySelector('script[data-paypal-sdk="true"]');
    if (existing) {
      const hasCardFields = existing.src.includes("card-fields");
      if (hasCardFields) { waitForPaypal(); return; }
      existing.remove();
      try { delete window.paypal; } catch (e) { window.paypal = undefined; }
    }
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(config.client_id)}&currency=${quote.quoted_currency || "USD"}&intent=capture&components=buttons,card-fields`;
    s.dataset.paypalSdk = "true";
    s.onload = () => waitForPaypal();
    s.onerror = () => { setStatus("error"); setErrMsg("Could not load PayPal — check your network or your popup blocker."); };
    document.body.appendChild(s);
  }, [config, quote.quoted_currency]);

  // Shared order creation + capture helpers used by both Buttons and Card Fields
  // Use refs so the useEffect that mounts PayPal isn't re-run when the parent
  // passes a new onPaid callback identity on every render.
  const quoteRef = useRef(quote);
  const onPaidRef = useRef(onPaid);
  useEffect(() => { quoteRef.current = quote; onPaidRef.current = onPaid; }, [quote, onPaid]);

  const createOrder = async () => {
    const q = quoteRef.current;
    const { data } = await api.post("/billing/paypal/create-order", {
      quote_request_id: q.id,
      amount: q.quoted_amount,
      currency: q.quoted_currency || "USD",
    });
    return data.order_id;
  };
  const onApprove = async ({ orderID }) => {
    try {
      const { data: cap } = await api.post(`/billing/paypal/capture/${orderID}`);
      if (cap.status === "COMPLETED") { setStatus("success"); onPaidRef.current?.(); }
      else { setStatus("error"); setErrMsg(`PayPal returned status: ${cap.status}`); }
    } catch (e) { setStatus("error"); setErrMsg(e?.response?.data?.detail || "Capture failed"); }
  };

  // Render Smart Buttons + Card Fields once SDK is ready
  useEffect(() => {
    if (status !== "ready" || !window.paypal) return;

    // ----- PayPal yellow button (popup) -----
    if (buttonsRef.current) {
      buttonsRef.current.innerHTML = "";
      try {
        window.paypal.Buttons({
          style: { layout: "horizontal", color: "gold", shape: "rect", label: "pay", height: 45, tagline: false },
          createOrder,
          onApprove,
          onError: (err) => { setStatus("error"); setErrMsg(String(err)?.slice(0, 200)); },
        }).render(buttonsRef.current);
      } catch (e) { console.warn("PayPal Buttons failed to render — card form remains primary path", e); }
    }

    // ----- Inline credit/debit card fields (no popup, no PayPal account required) -----
    if (!window.paypal.CardFields) { setCardEligible(false); return; }
    let cf;
    try {
      cf = window.paypal.CardFields({
        createOrder,
        onApprove,
        onError: (err) => { setStatus("error"); setErrMsg(String(err)?.slice(0, 200) || "Card payment failed"); setSubmitting(false); },
        style: {
          input: { "font-size": "14px", "font-family": "inherit", color: "#0A0A0A" },
          ".invalid": { color: "#D90429" },
        },
      });
    } catch (e) { setCardEligible(false); return; }
    cardFieldRef.current = cf;

    if (!cf.isEligible()) { setCardEligible(false); return; }
    setCardEligible(true);
    // Mount each field into its slot
    try {
      cf.NumberField({ placeholder: "1234 1234 1234 1234" }).render(cardNumRef.current);
      cf.ExpiryField({ placeholder: "MM / YY" }).render(cardExpRef.current);
      cf.CVVField({ placeholder: "CVV" }).render(cardCvvRef.current);
      cf.NameField({ placeholder: "Name on card" }).render(cardNameRef.current);
    } catch (e) { setCardEligible(false); }
  }, [status]);  // intentionally NOT depending on quote/onPaid — they're read via refs to prevent remount loops

  const submitCardForm = async (e) => {
    e.preventDefault();
    if (!cardFieldRef.current) return;
    setErrMsg("");
    setSubmitting(true);
    try {
      await cardFieldRef.current.submit();
      // onApprove will fire and flip status to success
    } catch (err) {
      setStatus("error");
      setErrMsg("Card was declined or could not be processed. Please check the details and try again.");
    } finally { setSubmitting(false); }
  };

  if (!config) return <div className="mt-8 text-sm text-[#495057]">Loading payment options…</div>;
  if (!config.configured) {
    return (
      <div className="mt-8 border border-[#FFB703] bg-[#FFB703]/10 p-6" data-testid="paypal-not-configured">
        <div className="flex items-start gap-3">
          <ShieldCheck size={18} className="text-[#0A0A0A] mt-0.5" />
          <div>
            <div className="font-medium">Your quote of ${quote.quoted_amount?.toFixed(2)} {quote.quoted_currency} is ready.</div>
            <div className="text-sm text-[#495057] mt-1">Payments aren&apos;t yet configured. Please contact support to complete your activation.</div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="mt-10 border border-[#0B7A75] bg-white p-6 flex items-center gap-2 text-[#0B7A75] font-medium" data-testid="paypal-success">
        <Check size={16} /> Payment confirmed — your workspace is now active. Reloading…
      </div>
    );
  }

  return (
    <div className="mt-10 border border-[#0A0A0A] bg-white" data-testid="payment-section">
      <div className="border-b border-[#DEE2E6] p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#0B7A75] font-mono"><Sparkles size={12} />Step 2 of 2</div>
        <h3 className="font-heading text-2xl font-bold tracking-tighter mt-1.5">Pay {quote.quoted_currency} {quote.quoted_amount?.toFixed(2)} — one year of full access</h3>
        <p className="text-sm text-[#495057] mt-1.5">Pay securely with your credit/debit card below — no PayPal account required. Your workspace upgrades to <strong>Active</strong> immediately on confirmation.</p>
      </div>

      <div className="p-6">
        {status === "error" && (
          <div className="mb-4 text-sm text-[#D90429] border border-[#D90429]/30 bg-[#D90429]/5 px-4 py-3" data-testid="paypal-error">{errMsg || "Payment failed. Please try again."}</div>
        )}

        {/* ----- Card Fields (primary, inline) ----- */}
        {cardEligible !== false && (
          <form onSubmit={submitCardForm} ref={cardFormRef} className="space-y-4" data-testid="card-form">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-[#495057] font-mono mb-2">Card number</label>
              <div ref={cardNumRef} className="border border-[#0A0A0A] bg-white px-3 h-[46px] flex items-center" data-testid="card-num-field" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#495057] font-mono mb-2">Expiry</label>
                <div ref={cardExpRef} className="border border-[#0A0A0A] bg-white px-3 h-[46px] flex items-center" data-testid="card-exp-field" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#495057] font-mono mb-2">CVV</label>
                <div ref={cardCvvRef} className="border border-[#0A0A0A] bg-white px-3 h-[46px] flex items-center" data-testid="card-cvv-field" />
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-[#495057] font-mono mb-2">Name on card</label>
              <div ref={cardNameRef} className="border border-[#0A0A0A] bg-white px-3 h-[46px] flex items-center" data-testid="card-name-field" />
            </div>
            <button type="submit" disabled={submitting || status !== "ready"} data-testid="card-pay-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white px-6 py-3.5 text-sm font-medium tracking-wide uppercase">
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Processing payment…</> : <>Pay {quote.quoted_currency} {quote.quoted_amount?.toFixed(2)}</>}
            </button>
            <div className="text-[11px] text-[#495057] flex items-center gap-1.5 justify-center">
              <ShieldCheck size={11} className="text-[#0B7A75]" />
              Secured by PayPal · {config.mode === "live" ? "Live" : "Sandbox"} · Card details never touch our servers
            </div>
          </form>
        )}

        {/* ----- OR — PayPal account option (secondary) ----- */}
        <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#495057] font-mono">
          <div className="flex-1 h-px bg-[#DEE2E6]" />
          <span>or pay with PayPal account</span>
          <div className="flex-1 h-px bg-[#DEE2E6]" />
        </div>
        <div ref={buttonsRef} data-testid="paypal-buttons" />
      </div>
    </div>
  );
}

function QuoteHistory({ quotes }) {
  return (
    <div className="mt-10" data-testid="quote-history">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#495057] font-mono">Quote history</div>
      <div className="mt-3 border border-[#DEE2E6] bg-white divide-y divide-[#DEE2E6]">
        {quotes.map((q) => (
          <div key={q.id} className="p-4 flex items-center justify-between gap-3 text-sm">
            <div className="min-w-0">
              <div className="font-medium truncate">{q.contact_name} · {q.contact_email}</div>
              <div className="text-xs text-[#495057] mt-0.5">{q.company_size || "?"} · {q.industry || "?"} · {q.estimated_users || "?"} users · {q.created_at?.slice(0, 10)}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {q.quoted_amount && <div className="text-sm">{q.quoted_currency} {q.quoted_amount.toFixed(2)}</div>}
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${
                q.status === "paid" ? "bg-[#0B7A75]/10 text-[#0B7A75]" :
                q.status === "quoted" ? "bg-[#FFB703]/15 text-[#0A0A0A]" :
                q.status === "declined" ? "bg-[#D90429]/10 text-[#D90429]" :
                "bg-[#DEE2E6] text-[#495057]"
              }`}>{q.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ----- form atoms (kept colocated to avoid extra files) ----- */
function FormField({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">{label}{required && " *"}</span>
      <input value={value} type={type} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
    </label>
  );
}

function FormSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
